import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from 'next-themes';
import { ModernChart } from './ModernChart';
import { ModernPlayback } from './ModernPlayback';
import { AssetManager } from './AssetManager';
import { ScannerDashboard } from '@/components/scanner/ScannerDashboard';
import { ProScannerDashboard } from '@/components/scanner/ProScannerDashboard';
import { CustomDashboard } from '@/components/scanner/CustomDashboard';
import { StrategyDashboard } from '@/components/strategy/StrategyDashboard';
import { StrategiesPage } from '@/components/automation/StrategiesPage';
import { ScenarioBacktestPage } from '@/components/automation/ScenarioBacktestPage';
import { TradeViewerPage } from '@/components/trade-viewer/TradeViewerPage';
import { PaperTradingShell } from '@/components/paper-trading/PaperTradingShell';
import { LiveTradingPage } from '@/components/live-trading/LiveTradingPage';
import { ConfigurationPage } from '@/components/configuration/ConfigurationPage';
import { UserManagementPage } from '@/components/admin/UserManagementPage';
import { AiAssistantPage } from '@/components/ai/AiAssistantPage';
import { AiSchedulesPage } from '@/components/ai/AiSchedulesPage';
import { UserMenu } from '@/components/auth/UserMenu';
import { useAuth } from '@/auth/AuthContext';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import {
  Play,
  Menu,
  X,
  Clock,
  Moon,
  Sun,
  Zap,
  Wifi,
  WifiOff,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Monitor,
  LayoutGrid,
  Settings2,
  Target,
  Bot,
  FlaskConical,
  Eye,
  Banknote,
  CircleDollarSign,
  Settings,
  Users,
  Sparkles,
  CalendarClock,
} from 'lucide-react';
import signalRService from '@/services/signalRService';
import type { MarketIndexSnapshot } from '@/apiService';

type TabType = 'scanner' | 'pro-scanner' | 'custom-dashboard' | 'strategies' | 'playback' | 'assets' | 'automation' | 'scenarios' | 'trade-viewer' | 'paper-trading' | 'live-trading' | 'ai-assistant' | 'ai-schedules' | 'configuration' | 'users';

const tabs: { id: TabType; label: string; icon: React.FC<any>; shortcut: string; description: string; superAdminOnly?: boolean; adminOnly?: boolean }[] = [
  { id: 'scanner', label: 'Scanner', icon: Zap, shortcut: '1', description: 'Real-time stock scanner' },
  { id: 'pro-scanner', label: 'Pro Scanner', icon: Monitor, shortcut: '2', description: 'DAS-style multi-panel scanner' },
  { id: 'custom-dashboard', label: 'My Dashboard', icon: LayoutGrid, shortcut: '3', description: 'Custom scanner dashboard' },
  { id: 'strategies', label: 'Strategies', icon: Target, shortcut: '4', description: 'Trade Ideas-style strategies' },
  { id: 'playback', label: 'Playback', icon: Play, shortcut: '5', description: 'Historical replay', superAdminOnly: true },
  { id: 'automation', label: 'Automation', icon: Bot, shortcut: 'A', description: 'Scanner-triggered trading' },
  { id: 'scenarios', label: 'Scenarios', icon: FlaskConical, shortcut: 'B', description: 'Backtest combinations' },
  { id: 'trade-viewer', label: 'Trade Viewer', icon: Eye, shortcut: 'C', description: 'Inspect individual backtest trades' },
  { id: 'paper-trading', label: 'Paper Trading', icon: Banknote, shortcut: 'D', description: 'Live paper account + automation trades' },
  { id: 'live-trading', label: 'Live Trading', icon: CircleDollarSign, shortcut: 'E', description: 'Real-money forex (FXCM)' },
  { id: 'ai-assistant', label: 'AI Assistant', icon: Sparkles, shortcut: 'I', description: 'Chat with the AI trading assistant' },
  { id: 'ai-schedules', label: 'AI Schedules', icon: CalendarClock, shortcut: 'L', description: 'Loops & one-shot AI schedules' },
  { id: 'assets', label: 'Assets', icon: Settings2, shortcut: '9', description: 'Manage subscriptions', superAdminOnly: true },
  { id: 'configuration', label: 'Configuration', icon: Settings, shortcut: ',', description: 'Settings & integrations' },
  { id: 'users', label: 'Users', icon: Users, shortcut: 'U', description: 'User management', adminOnly: true },
];

// Read initial tab from URL hash so shareable links land on the right tab.
// Format: #/dashboard/<tab>?<query>  e.g. #/dashboard/trade-viewer?scenario=abc&trade=42
// Bare #/dashboard (no tab segment) resolves to the default 'scanner' tab.
function readTabFromHash(): TabType {
  const raw = window.location.hash.replace(/^#\/?/, '');
  const beforeQuery = raw.split('?')[0];
  // Strip the mandatory dashboard prefix; what remains is the tab path.
  const afterPrefix = beforeQuery.startsWith('dashboard/')
    ? beforeQuery.slice('dashboard/'.length)
    : beforeQuery === 'dashboard'
      ? ''
      : beforeQuery; // shouldn't happen — App.tsx routes only land here for dashboard URLs
  // Subroutes like dashboard/configuration/paper-trading still resolve to the
  // 'configuration' tab — ConfigurationPage parses the deeper segments itself.
  const tabId = afterPrefix.split('/')[0] as TabType;
  const validTabs: TabType[] = [
    'scanner', 'pro-scanner', 'custom-dashboard', 'strategies',
    'playback', 'assets',
    'automation', 'scenarios', 'trade-viewer', 'paper-trading', 'live-trading',
    'ai-assistant', 'ai-schedules', 'configuration', 'users',
  ];
  return validTabs.includes(tabId) ? tabId : 'scanner';
}

export const ModernDashboard: React.FC = () => {
  const [activeTab, setActiveTabState] = useState<TabType>(() => readTabFromHash());
  const { user } = useAuth();
  const isSuperAdmin = user?.roles?.includes('SuperAdmin') ?? false;
  // adminOnly tabs (e.g. user management) are for AccountManager and above —
  // matching the AccountManagerOrAbove policy on the backend endpoints.
  const isAdmin = isSuperAdmin || (user?.roles?.includes('AccountManager') ?? false);
  // Hide gated tabs from non-admins. The backend gates the actual endpoints —
  // this just removes the affordance from the sidebar.
  const visibleTabs = tabs.filter(t =>
    (!t.superAdminOnly || isSuperAdmin) && (!t.adminOnly || isAdmin));

  // Bounce non-admins off SuperAdmin-only tabs even if they hash-link directly.
  // Backend already returns 401/403 for the endpoints those pages call, but
  // rendering the page shell + flashing API errors is a worse UX than just
  // redirecting to the default tab.
  useEffect(() => {
    const targetTab = tabs.find(t => t.id === activeTab);
    if ((targetTab?.superAdminOnly && !isSuperAdmin)
        || (targetTab?.adminOnly && !isAdmin)) {
      setActiveTab('scanner');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isSuperAdmin, isAdmin]);

  // Wrap setActiveTab so it also updates the URL hash, preserving any query string.
  // Tab 'scanner' is the default and renders as bare #/dashboard.
  const setActiveTab = (tab: TabType) => {
    setActiveTabState(tab);
    // Preserve existing query params (e.g. scenario id) on tab change
    const queryPart = window.location.hash.includes('?')
      ? '?' + window.location.hash.split('?')[1]
      : '';
    const newHash = `#/dashboard${tab === 'scanner' ? '' : '/' + tab}${queryPart}`;
    if (window.location.hash !== newHash) {
      // Use history.replaceState so it doesn't pollute the back-button stack
      window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
    }
  };

  // Listen for browser hash changes (back/forward buttons or paste-in URLs)
  useEffect(() => {
    const onHash = () => setActiveTabState(readTabFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('AAPL');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
  const [marketIndices, setMarketIndices] = useState<MarketIndexSnapshot[]>([]);
  const { theme, setTheme } = useTheme();

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Receive market indices updates via SignalR (server pushes every 10s)
  useEffect(() => {
    // Subscribe to real-time SignalR updates (server pushes every 10s)
    signalRService.addMarketIndicesListener('modern-dashboard', (indices) => {
      setMarketIndices(indices);
    });

    // Also listen for backtest market indices (overrides live when active)
    signalRService.addBacktestMarketIndicesListener('modern-dashboard', (indices) => {
      setMarketIndices(indices);
    });

    return () => {
      signalRService.removeMarketIndicesListener('modern-dashboard');
      signalRService.removeBacktestMarketIndicesListener('modern-dashboard');
    };
  }, []);

  // Monitor SignalR connection
  useEffect(() => {
    const checkConnection = () => {
      setConnectionStatus(signalRService.isConnected() ? 'connected' : 'disconnected');
    };
    checkConnection();
    const interval = setInterval(checkConnection, 3000);
    return () => clearInterval(interval);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        if (visibleTabs[index]) setActiveTab(visibleTabs[index].id);
      }
      if (e.altKey && e.key === 'b') {
        e.preventDefault();
        setSidebarCollapsed(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSymbolSelect = useCallback((symbol: string) => {
    setSelectedSymbol(symbol);
  }, []);

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/New_York' });

  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'America/New_York' });

  const marketSession = useMemo(() => {
    const now = new Date();
    const estStr = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
    const est = new Date(estStr);
    const day = est.getDay();
    if (day === 0 || day === 6) return 'closed' as const;
    const mins = est.getHours() * 60 + est.getMinutes();
    // Pre-Market: 4:00 AM (240) – 9:30 AM (570)
    if (mins >= 240 && mins < 570) return 'premarket' as const;
    // Regular: 9:30 AM (570) – 4:00 PM (960)
    if (mins >= 570 && mins < 960) return 'open' as const;
    // After-Hours: 4:00 PM (960) – 8:00 PM (1200)
    if (mins >= 960 && mins < 1200) return 'afterhours' as const;
    return 'closed' as const;
  }, [Math.floor(currentTime.getTime() / 60000)]);

  const marketStatusConfig = {
    open:       { label: 'Market Open',   color: 'bg-emerald-500', pulse: true },
    premarket:  { label: 'PreMarket',     color: 'bg-amber-500',   pulse: true },
    afterhours: { label: 'After Hours',   color: 'bg-blue-500',    pulse: true },
    closed:     { label: 'Market Closed', color: 'bg-amber-500',   pulse: false },
  } as const;
  const msCfg = marketStatusConfig[marketSession];

  const connectionColor = connectionStatus === 'connected' ? 'text-emerald-500' : 'text-red-400';
  const connectionBg = connectionStatus === 'connected' ? 'bg-emerald-500' : 'bg-red-400';

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* ═══ HEADER BAR ═══ */}
      <header className="h-12 border-b glass-strong flex items-center justify-between px-4 shrink-0 z-20">
        <div className="flex items-center gap-3">
          {/* Mobile menu */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden h-8 w-8"
          >
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>

          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center shadow-lg shadow-primary/20">
              <TrendingUp className="h-4 w-4 text-white" />
            </div>
            <div className="hidden sm:block">
              <span className="text-sm font-bold tracking-tight">Stock</span>
              <span className="text-sm font-bold tracking-tight text-primary">Screener</span>
              <span className="text-[10px] font-medium text-primary ml-1 bg-primary/10 px-1.5 py-0.5 rounded-full">PRO</span>
            </div>
          </div>

          <div className="h-5 w-px bg-border hidden md:block" />

          {/* Market Status */}
          <div className="hidden md:flex items-center gap-2">
            <div className={`h-2 w-2 rounded-full ${msCfg.color} ${msCfg.pulse ? 'pulse-live' : ''}`} />
            <span className="text-xs text-muted-foreground">
              {msCfg.label}
            </span>
          </div>

          {/* Market Indices Ticker */}
          <div className="hidden lg:flex items-center gap-4 ml-2 overflow-hidden">
            {(marketIndices.length > 0 ? marketIndices : [
              { symbol: 'SPY', changePercent: 0 },
              { symbol: 'QQQ', changePercent: 0 },
              { symbol: 'DIA', changePercent: 0 },
              { symbol: 'IWM', changePercent: 0 },
            ]).map((idx) => (
              <div key={idx.symbol} className="flex items-center gap-1.5 text-xs">
                <span className="text-muted-foreground font-medium">{idx.symbol}</span>
                <span className={`font-mono tabular-nums font-medium ${idx.changePercent >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                  {idx.changePercent >= 0 ? '+' : ''}{idx.changePercent.toFixed(2)}%
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Selected Symbol Chip */}
          <div className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/8 border border-primary/20">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span className="text-xs font-semibold text-primary font-mono">{selectedSymbol}</span>
          </div>

          {/* Connection */}
          <Tooltip content={`SignalR: ${connectionStatus}`}>
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${connectionStatus === 'connected' ? 'bg-emerald-500/10' : 'bg-red-400/10'}`}>
              {connectionStatus === 'connected' ? (
                <Wifi className="h-3 w-3 text-emerald-500" />
              ) : (
                <WifiOff className="h-3 w-3 text-red-400" />
              )}
              <span className={`text-[10px] font-medium hidden sm:inline ${connectionColor}`}>
                {connectionStatus === 'connected' ? 'Live' : 'Offline'}
              </span>
            </div>
          </Tooltip>

          {/* Clock (ET) */}
          <div className="hidden sm:flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-muted/50">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <span className="font-mono tabular-nums font-medium">{formatTime(currentTime)}</span>
            <span className="text-muted-foreground/70 hidden md:inline">{formatDate(currentTime)}</span>
            <span className="text-muted-foreground/50 text-[10px]">ET</span>
          </div>

          {/* Theme Toggle */}
          <Tooltip content={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="h-8 w-8 rounded-full"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </Tooltip>

          {/* User Menu (sign in / register / signed-in dropdown) */}
          <UserMenu />
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* ═══ SIDEBAR ═══ */}
        <aside className={`${
          sidebarCollapsed ? 'w-[60px]' : 'w-[220px]'
        } border-r bg-sidebar transition-all duration-200 hidden lg:flex flex-col shrink-0`}>
          <nav className="flex-1 py-3 px-2">
            <div className="space-y-1">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const navButton = (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 rounded-lg text-sm transition-all duration-150 group ${
                      sidebarCollapsed ? 'px-3 py-2.5 justify-center' : 'px-3 py-2.5'
                    } ${
                      isActive
                        ? 'bg-primary/10 text-primary font-medium nav-glow'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/60'
                    }`}
                  >
                    <Icon className={`h-4.5 w-4.5 shrink-0 transition-colors ${isActive ? 'text-primary' : 'group-hover:text-foreground'}`} />
                    {!sidebarCollapsed && (
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="truncate">{tab.label}</span>
                          <kbd className="hidden xl:inline text-[9px] text-muted-foreground/50 font-mono bg-muted/50 px-1 py-0.5 rounded">
                            {tab.shortcut}
                          </kbd>
                        </div>
                        {isActive && (
                          <span className="text-[10px] text-muted-foreground truncate block">{tab.description}</span>
                        )}
                      </div>
                    )}
                  </button>
                );

                return sidebarCollapsed ? (
                  <Tooltip key={tab.id} content={`${tab.label} (Alt+${tab.shortcut})`}>
                    {navButton}
                  </Tooltip>
                ) : (
                  <React.Fragment key={tab.id}>{navButton}</React.Fragment>
                );
              })}
            </div>
          </nav>

          {/* Sidebar collapse toggle */}
          <div className="border-t py-2 px-2">
            <Tooltip content={sidebarCollapsed ? "Expand (Alt+B)" : ""}>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="w-full flex items-center justify-center gap-2 rounded-lg px-2.5 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
              >
                {sidebarCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <>
                    <ChevronLeft className="h-4 w-4" />
                    <span>Collapse</span>
                  </>
                )}
              </button>
            </Tooltip>
          </div>
        </aside>

        {/* Mobile Navigation Overlay */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 top-12 z-30">
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
            <nav className="relative w-72 h-full overflow-y-auto overscroll-contain bg-card border-r p-4 space-y-1 slide-in-left shadow-2xl">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 px-3">Navigation</div>
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors ${
                      isActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <div className="text-left">
                      <div>{tab.label}</div>
                      <div className="text-[10px] text-muted-foreground">{tab.description}</div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* ═══ MAIN CONTENT ═══ */}
        <main className="flex-1 min-w-0 overflow-auto scrollbar-thin bg-background">
          <div className="fade-in h-full">
            <ErrorBoundary>
              {activeTab === 'scanner' && (
                <ScannerDashboard onSymbolSelect={handleSymbolSelect} />
              )}

              {activeTab === 'pro-scanner' && (
                <ProScannerDashboard onSymbolSelect={handleSymbolSelect} />
              )}

              {activeTab === 'custom-dashboard' && (
                <CustomDashboard onSymbolSelect={handleSymbolSelect} />
              )}

              {activeTab === 'strategies' && (
                <StrategyDashboard />
              )}

              {activeTab === 'playback' && (
                <div className="p-5 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Play className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">Historical Playback</h2>
                      <span className="text-xs text-muted-foreground">Replay market data at any speed</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                    <div className="xl:col-span-2">
                      <ErrorBoundary>
                        <ModernChart key="playback-AAPL-1M" symbol="AAPL" timeframe="1M" className="w-full" />
                      </ErrorBoundary>
                    </div>
                    <div>
                      <ModernPlayback />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'assets' && (
                <div className="p-5 space-y-5">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Settings2 className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">Asset Manager</h2>
                      <span className="text-xs text-muted-foreground">Manage WebSocket subscriptions & browse assets</span>
                    </div>
                  </div>
                  <AssetManager onSymbolSelect={handleSymbolSelect} />
                </div>
              )}

              {activeTab === 'automation' && (
                <div className="p-5">
                  <StrategiesPage />
                </div>
              )}

              {activeTab === 'scenarios' && (
                <div className="p-5">
                  <ScenarioBacktestPage />
                </div>
              )}

              {activeTab === 'trade-viewer' && (
                <TradeViewerPage />
              )}

              {activeTab === 'paper-trading' && (
                <PaperTradingShell />
              )}

              {activeTab === 'live-trading' && (
                <LiveTradingPage />
              )}

              {activeTab === 'ai-assistant' && (
                <AiAssistantPage />
              )}

              {activeTab === 'ai-schedules' && (
                <AiSchedulesPage />
              )}

              {activeTab === 'configuration' && (
                <ConfigurationPage />
              )}

              {activeTab === 'users' && (
                <UserManagementPage />
              )}
            </ErrorBoundary>
          </div>
        </main>
      </div>

      {/* ═══ STATUS BAR ═══ */}
      <footer className="h-7 border-t glass-strong flex items-center justify-between px-4 text-[11px] text-muted-foreground shrink-0 z-10">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className={`h-1.5 w-1.5 rounded-full ${connectionBg} ${connectionStatus === 'connected' ? 'pulse-live' : ''}`} />
            <span className="font-medium">{connectionStatus === 'connected' ? 'Live' : 'Offline'}</span>
          </div>
          <span className="text-muted-foreground/30">|</span>
          <span>TradingView Charts</span>
          <span className="text-muted-foreground/30">|</span>
          <span>SignalR Streaming</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline opacity-60">Alt+[1-9] Switch tabs</span>
          <span className="hidden sm:inline text-muted-foreground/30">|</span>
          <span className="hidden sm:inline opacity-60">Alt+B Toggle sidebar</span>
        </div>
      </footer>
    </div>
  );
};

export default ModernDashboard;
